import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import SystemMessageCard from '@/components/SystemMessages/SystemMessageCard';
import { iconForMessage, systemMessageIconKeys, systemMessageIcons } from '@/components/SystemMessages/systemMessageIcons';
import { AnnouncementImage } from '@/components/Announcements/AnnouncementImage';
import {
  SystemMessageAudience,
  SystemMessageBlock,
  SystemMessageCategory,
  SystemMessageDtoIn,
  SystemMessageIconKey,
  SystemMessageTourStep,
  SystemMessageType,
} from '@/models/in/SystemMessageDtoIn';
import { SystemMessageDtoOut } from '@/models/out/SystemMessageDtoOut';
import { FeatureFlagDtoIn } from '@/models/in/FeatureFlagDtoIn';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { postAuth, putAuth } from '@/api/ApiCalls';
import SystemMessageBlocksEditor from './SystemMessageBlocksEditor';
import { SYSTEM_MESSAGES_MANAGEMENT_URL, SYSTEM_MESSAGE_TYPES, cleanBlocks, firstUnsafeButtonUrl, toDateInputValue } from './systemMessages';
import styles from './EditOrCreateSystemMessageDialog.module.scss';
import bellStyles from '@/components/SystemMessages/UpdatesBell.module.scss';
import bubbleStyles from '@/components/Announcements/AnnouncementTour.module.scss';

interface EditOrCreateSystemMessageDialogProps {
  open: boolean;
  onClose: () => void;
  messageToEdit?: SystemMessageDtoIn | null;
  onSuccess: () => void;
  /**
   * A new message for a feature (HAT-1489) - «+ הוספת הודעה» in the feature's release leads here:
   * linked to that flag, for that audience. Ignored when editing - a message never moves to another flag.
   */
  linkTo?: { featureFlagKey: string; audience: SystemMessageAudience } | null;
  /**
   * Opened from a feature's release («לעריכה» there): closing or saving goes back to it, and the
   * buttons say so. The page does the going back, in its onClose.
   */
  returnsToRelease?: boolean;
}

type Surface = 'popup' | 'bell' | 'bubble';
type FieldError = 'title' | 'dates' | 'blocks' | 'save';

/**
 * The message editor (HAT-1489 rework), in two modes. A feature's announcement came with the code:
 * its flag and audience are facts, its type, icon and dates are the feature's, and only the wording
 * is edited here. A message the office writes from scratch has the whole form. Each error sits under
 * its field, and the preview shows the message on every surface it reaches.
 */
const editorTitle = (t: (key: string, options?: Record<string, unknown>) => string, isEditing: boolean, linkedAudience: SystemMessageAudience | null) =>
  linkedAudience
    ? t(isEditing ? 'systemMessages.dialog.editFeatureTitle' : 'systemMessages.dialog.createFeatureTitle', { audience: t(`announcements.audience.${linkedAudience}`) })
    : t(isEditing ? 'systemMessages.dialog.editTitle' : 'systemMessages.dialog.createTitle');

const EditOrCreateSystemMessageDialog = ({ open, onClose, messageToEdit, onSuccess, linkTo = null, returnsToRelease = false }: EditOrCreateSystemMessageDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [eyebrow, setEyebrow] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<SystemMessageType>('info');
  /*
   * אין בורר בטופס, ובכוונה: בלי סינון לפי קרן יש קטגוריה אחת בפועל, ו"עדכון קרן"
   * הבטיח נמען שהנתונים לא יכולים לספק - לטבלה אין עמודת קרן, ולכן הודעה על קרן אחת
   * הייתה מוצגת לרכזים של כולן. ה-state נשמר כדי שעריכה לא תדרוס ערך שכבר מאוחסן.
   */
  const [category] = useState<SystemMessageCategory>('system');
  const [iconKey, setIconKey] = useState<SystemMessageIconKey | null>(null);
  const [blocks, setBlocks] = useState<SystemMessageBlock[]>([]);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [validityOpen, setValidityOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [showAsPopup, setShowAsPopup] = useState(true);
  const [tour, setTour] = useState<SystemMessageTourStep[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [surface, setSurface] = useState<Surface>('popup');
  const [error, setError] = useState<{ field: FieldError; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!messageToEdit;

  /*
   * The flag a message belongs to: shown, not edited. It was set with the feature, and moving a
   * message to another flag would announce one feature with another's words.
   */
  const linkedKey = messageToEdit ? messageToEdit.featureFlagKey : linkTo?.featureFlagKey ?? null;
  // Whom the message is for - picked on creation and changeable later, on any message.
  const [audience, setAudience] = useState<SystemMessageAudience>('all');
  const isFeatureMessage = !!linkedKey;
  const { data: flags } = useGetAuthApi<FeatureFlagDtoIn[]>(open && linkedKey ? 'management/feature-flags' : '');
  const linkedName = flags?.find((flag) => flag.featureKey === linkedKey)?.displayName ?? linkedKey;
  // The feature's other messages, so the editor can say this is one side of it and name the other.
  const { data: allMessages } = useGetAuthApi<SystemMessageDtoIn[]>(open && linkedKey ? SYSTEM_MESSAGES_MANAGEMENT_URL : '');
  const siblings = (allMessages ?? []).filter((message) => message.featureFlagKey === linkedKey && message.id !== messageToEdit?.id);

  useEffect(() => {
    if (!open) return;

    if (messageToEdit) {
      setEyebrow(messageToEdit.eyebrow ?? '');
      setTitle(messageToEdit.title);
      setBody(messageToEdit.body ?? '');
      setType(messageToEdit.type);
      setIconKey(messageToEdit.iconKey);
      setBlocks(messageToEdit.blocks ?? []);
      setValidFrom(toDateInputValue(messageToEdit.validFrom));
      setValidTo(toDateInputValue(messageToEdit.validTo));
      setValidityOpen(!!messageToEdit.validFrom || !!messageToEdit.validTo);
      setIsActive(messageToEdit.isActive);
      setShowAsPopup(messageToEdit.showAsPopup ?? true);
      setTour(messageToEdit.tour ?? []);
      setAudience(messageToEdit.audience ?? 'all');
    } else {
      setAudience(linkTo?.audience ?? 'all');
      setEyebrow(linkTo ? t('systemMessages.editor.defaultEyebrow') : '');
      setTitle('');
      setBody('');
      setType('info');
      // A feature's announcement waits in the bell and on the screen unless the office asks it to
      // interrupt - the same default the seeds use.
      setShowAsPopup(!linkTo);
      setTour([]);
      setIconKey(null);
      setBlocks([]);
      setValidFrom('');
      setValidTo('');
      setValidityOpen(false);
      setIsActive(true);
    }
    setMoreOpen(false);
    setSurface('popup');
    setError(null);
    // By the link's content, not its identity: a parent passing a fresh object each render must not
    // reset what the admin is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messageToEdit, linkTo?.featureFlagKey, linkTo?.audience]);

  // The preview shows what would actually be stored, so an admin sees the half-filled row they
  // just added disappear the moment it stops being empty-or-not.
  const preview = useMemo(
    () => ({
      eyebrow: eyebrow.trim() || null,
      title: title.trim() || t('systemMessages.editor.untitled'),
      body: body.trim() || null,
      type,
      iconKey,
      blocks: cleanBlocks(blocks),
    }),
    [eyebrow, title, body, type, iconKey, blocks, t]
  );

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const clearError = () => {
    if (error) setError(null);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError({ field: 'title', text: t('systemMessages.dialog.titleRequired') });
      return;
    }
    if (validFrom && validTo && new Date(validTo) < new Date(validFrom)) {
      setValidityOpen(true);
      setError({ field: 'dates', text: t('systemMessages.dialog.invalidDates') });
      return;
    }

    const unsafeUrl = firstUnsafeButtonUrl(blocks);
    if (unsafeUrl) {
      setError({ field: 'blocks', text: t('systemMessages.dialog.unsafeUrl', { url: unsafeUrl }) });
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const requestBody: SystemMessageDtoOut = {
      eyebrow: eyebrow.trim() || null,
      title: title.trim(),
      body: body.trim() || null,
      type,
      category,
      showAsPopup,
      iconKey,
      blocks: cleanBlocks(blocks),
      // An empty date input means "unbounded on that side", which the API reads as null.
      validFrom: validFrom || null,
      validTo: validTo || null,
      isActive,
      audience,
      // The steps are wording, sent only for a message that has them to keep; the flag only on
      // creation, which is the one time the server reads it.
      ...(linkedKey ? { tour: tour.filter((step) => step.text.trim()).map((step) => ({ ...step, text: step.text.trim(), title: step.title?.trim() || null })) } : {}),
      ...(!isEditing && linkTo ? { featureFlagKey: linkTo.featureFlagKey } : {}),
    };

    try {
      if (isEditing) {
        await putAuth<SystemMessageDtoIn, object, SystemMessageDtoOut>(`${SYSTEM_MESSAGES_MANAGEMENT_URL}/${messageToEdit!.id}`, headers, {}, requestBody);
      } else {
        await postAuth<SystemMessageDtoIn, SystemMessageDtoOut>(SYSTEM_MESSAGES_MANAGEMENT_URL, headers, requestBody);
      }
      onSuccess();
      handleClose();
    } catch {
      setError({ field: 'save', text: t(isEditing ? 'systemMessages.dialog.errorUpdating' : 'systemMessages.dialog.errorCreating') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldError = (field: FieldError) => error?.field === field && <p className={`${styles.error} body-xs-sm-regular`}>{error.text}</p>;

  const dialogTitle = editorTitle(t, isEditing, isFeatureMessage ? audience : null);

  /*
   * לכולם · לרכזים · למשרד. On a feature's message, an audience another message of the same feature
   * already has is taken: a feature gets one message per side, and the line beside says which.
   */
  const audienceChips = (
    <div className={styles.typeChips} role="radiogroup" aria-label={t('systemMessages.audience')}>
      {(['all', 'coordinators', 'office'] as SystemMessageAudience[]).map((option) => {
        const taken = siblings.some((sibling) => sibling.audience === option);
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={audience === option}
            disabled={taken}
            className={`${styles.typeChip} ${styles.audienceChip} ${audience === option ? styles.typeChipActive : ''} body-xs-semibold`}
            onClick={() => setAudience(option)}
          >
            {t(`announcements.audience.${option}`)}
          </button>
        );
      })}
    </div>
  );

  const validityLabel = () => {
    if (!validFrom && !validTo) return t('systemMessages.noLimit');
    const from = validFrom ? new Date(validFrom).toLocaleDateString('he-IL') : '';
    const to = validTo ? new Date(validTo).toLocaleDateString('he-IL') : '';
    if (from && to) return `${from}–${to}`;
    return from ? t('systemMessages.validity.from', { date: from }) : t('systemMessages.validity.to', { date: to });
  };

  const typeAndIcon = (
    <>
      <div className={styles.field}>
        <span className={`${styles.label} body-sm-semibold`}>{t('systemMessages.type')}</span>
        {/* The colour is on the chip itself, so nothing has to explain which type is which. */}
        <div className={styles.typeChips} role="radiogroup">
          {SYSTEM_MESSAGE_TYPES.map((messageType) => (
            <button
              key={messageType}
              type="button"
              role="radio"
              aria-checked={type === messageType}
              className={`${styles.typeChip} ${styles[`type-${messageType}`]} ${type === messageType ? styles.typeChipActive : ''} body-sm-semibold`}
              onClick={() => { setType(messageType); clearError(); }}
            >
              {t(`systemMessages.types.${messageType}`)}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.field}>
        <span className={`${styles.label} body-sm-semibold`}>{t('systemMessages.editor.icon')}</span>
        <div className={styles.iconGrid}>
          {/* The first option hands the choice back to the type's own icon. */}
          <button
            type="button"
            className={`${styles.iconOption} ${iconKey === null ? styles.iconOptionActive : ''}`}
            onClick={() => setIconKey(null)}
            aria-label={t('systemMessages.editor.iconAuto')}
            aria-pressed={iconKey === null}
          >
            <svg className={styles.iconGlyph} viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 19 19 5M5 5l14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
              <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
          {systemMessageIconKeys.map((key) => (
            <button
              key={key}
              type="button"
              className={`${styles.iconOption} ${iconKey === key ? styles.iconOptionActive : ''}`}
              onClick={() => setIconKey(key)}
              aria-label={t(`systemMessages.editor.icons.${key}`)}
              aria-pressed={iconKey === key}
            >
              <svg className={styles.iconGlyph} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                {systemMessageIcons[key]}
              </svg>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const eyebrowField = (
    <div className={styles.field}>
      <label htmlFor="sm-eyebrow" className={`${styles.label} body-sm-semibold`}>{t('systemMessages.eyebrow')}</label>
      <input
        id="sm-eyebrow"
        type="text"
        maxLength={80}
        className={`${styles.input} body-sm-regular`}
        value={eyebrow}
        onChange={(e) => { setEyebrow(e.target.value); clearError(); }}
        placeholder={t('systemMessages.editor.eyebrowPlaceholder')}
      />
    </div>
  );

  const bellPreview = (
    <div className={`${bellStyles.noteUnread} ${styles.bellNote}`}>
      <span className={bellStyles.avatar}>
        <svg className={bellStyles.avatarIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          {iconForMessage(preview)}
        </svg>
      </span>
      <div className={bellStyles.noteBody}>
        <span className={`body-sm-semibold ${bellStyles.noteTitle}`}>{preview.title}</span>
        {preview.body && <span className={`body-sm-regular ${bellStyles.noteTextOpen}`}>{preview.body}</span>}
        {preview.blocks.length > 0 && (
          <div className={bellStyles.noteBlocks}>
            {preview.blocks.map((block, index) => {
              if (block.kind === 'image' && block.src) return <AnnouncementImage key={index} className={bellStyles.noteImage} src={block.src} alt={block.text ?? ''} isPreview />;
              if (block.kind === 'bullets') return <ul key={index} className={`body-sm-regular ${bellStyles.noteBullets}`}>{(block.items ?? []).map((item, i) => <li key={i}>{item}</li>)}</ul>;
              if (block.kind === 'buttons') return <div key={index} className={bellStyles.noteButtons}>{(block.buttons ?? []).map((button, i) => <span key={i} className={`body-sm-semibold ${button.variant === 'primary' ? bellStyles.noteButtonPrimary : bellStyles.noteButton}`}>{button.label}</span>)}</div>;
              if (block.kind === 'heading') return <span key={index} className={`body-sm-semibold ${bellStyles.noteTitle}`}>{block.text}</span>;
              return <p key={index} className={`body-sm-regular ${block.kind === 'callout' ? bellStyles.noteCallout : bellStyles.noteParagraph}`}>{block.text}</p>;
            })}
          </div>
        )}
        <span className={`body-xs-sm-regular ${bellStyles.stamp}`}>{t('updates.today', { time: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) })}</span>
      </div>
    </div>
  );

  const bubblePreview = tour.length > 0 && (
    <div className={`${bubbleStyles.bubble} ${styles.bubbleStatic}`}>
      <span className={bubbleStyles.accent} aria-hidden="true" />
      <span className={bubbleStyles.icon} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" focusable="false">{iconForMessage(preview)}</svg>
      </span>
      <div className={bubbleStyles.body}>
        <span className={`${bubbleStyles.title} body-md-semibold`}>{tour[0].title?.trim() || t('announcements.newTitle', { title: preview.title })}</span>
        <p className={`${bubbleStyles.text} body-sm-regular`}>{tour[0].text || t('systemMessages.editor.tourStepText')}</p>
        <div className={bubbleStyles.actions}>
          <span className={`${bubbleStyles.primary} body-sm-semibold`}>{tour.length > 1 ? t('announcements.next') : t('announcements.gotIt')}</span>
          {tour.length > 1 && <span className={`${bubbleStyles.step} body-xs-regular`}>{t('announcements.step', { current: 1, total: tour.length })}</span>}
        </div>
      </div>
    </div>
  );

  const content = (
      <div className={styles.layout}>
        <div className={styles.column}>
          {returnsToRelease && linkedKey && (
            <button type="button" className={`${styles.backLink} body-sm-semibold`} onClick={handleClose}>
              → {t('featureFlags.dialog.backToRelease', { name: linkedName })}
            </button>
          )}
          {/* One line of facts about a feature's announcement: whose, for whom, and where it is released. */}
          {linkedKey && (
            <div className={styles.linkedLine}>
              <span className="body-sm-regular text-(--font-body-2)">{t('systemMessages.aboutFeature')}</span>
              <span className="body-sm-semibold">{linkedName}</span>
              <span className={styles.dot} aria-hidden="true">·</span>
              {audienceChips}
              {siblings.map((sibling) => (
                <span key={sibling.id} className="body-sm-regular text-(--font-body-2)">
                  <span className={styles.dot} aria-hidden="true">·</span> {t('announcements.sibling', { audience: t(`announcements.audience.${sibling.audience}`) })}
                </span>
              ))}
              {/* The way to the release this message goes out with - unless that is where it came from, and goes back to anyway. */}
              {!returnsToRelease && (
                <>
                  <span className={styles.dot} aria-hidden="true">·</span>
                  <Link to={`/management/feature-flags?flag=${encodeURIComponent(linkedKey)}`} className={`${styles.flagLink} body-sm-semibold`}>
                    {t('systemMessages.toRelease')}
                  </Link>
                </>
              )}
            </div>
          )}

          {/* A message of its own, with no feature: whom it goes to is the first thing to decide. */}
          {!linkedKey && (
            <div className={styles.field}>
              <span className={`${styles.label} body-sm-semibold`}>{t('systemMessages.audience')}</span>
              {audienceChips}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="sm-title" className={`${styles.label} body-sm-semibold`}>{t('systemMessages.title')}</label>
            <input
              id="sm-title"
              type="text"
              maxLength={200}
              className={`${styles.input} body-sm-regular`}
              value={title}
              onChange={(e) => { setTitle(e.target.value); clearError(); }}
              placeholder={t('systemMessages.dialog.titlePlaceholder')}
              aria-invalid={error?.field === 'title' || undefined}
            />
            {fieldError('title')}
          </div>

          <div className={styles.field}>
            <label htmlFor="sm-body" className={`${styles.label} body-sm-semibold`}>{t('systemMessages.body')}</label>
            <textarea
              id="sm-body"
              rows={3}
              className={`${styles.input} body-sm-regular`}
              value={body}
              onChange={(e) => { setBody(e.target.value); clearError(); }}
              placeholder={t('systemMessages.dialog.bodyPlaceholder')}
            />
          </div>

          <div className={styles.field}>
            <span className={`${styles.label} body-sm-semibold`}>{t('systemMessages.editor.blocksSection')}</span>
            <SystemMessageBlocksEditor blocks={blocks} onChange={(next) => { setBlocks(next); clearError(); }} />
            {fieldError('blocks')}
          </div>

          {linkedKey && tour.length > 0 && (
            <div className={styles.field}>
              <span className={`${styles.label} body-sm-semibold`}>{t('systemMessages.editor.tourSection', { count: tour.length })}</span>
              {tour.map((step, index) => (
                <div key={index} className={styles.tourStep}>
                  {/* Where it points is code; the words say where that is on the screen. */}
                  <span className="body-sm-semibold">
                    {t('systemMessages.editor.tourStep', { number: index + 1 })}
                    <span className="body-xs-sm-regular text-(--font-body-3)"> · {t(`announcements.anchors.${step.anchor || linkedKey}`, { defaultValue: step.anchor || linkedKey })}</span>
                  </span>
                  <input
                    id={`sm-tour-title-${index}`}
                    type="text"
                    maxLength={80}
                    className={`${styles.input} body-sm-regular`}
                    value={step.title ?? ''}
                    onChange={(e) => setTour(tour.map((s, i) => (i === index ? { ...s, title: e.target.value } : s)))}
                    placeholder={t('systemMessages.editor.tourStepTitle')}
                    aria-label={t('systemMessages.editor.tourStepTitle')}
                  />
                  <textarea
                    id={`sm-tour-text-${index}`}
                    rows={2}
                    maxLength={160}
                    className={`${styles.input} body-sm-regular`}
                    value={step.text}
                    onChange={(e) => { setTour(tour.map((s, i) => (i === index ? { ...s, text: e.target.value } : s))); clearError(); }}
                    placeholder={t('systemMessages.editor.tourStepText')}
                    aria-label={t('systemMessages.editor.tourStepText')}
                  />
                </div>
              ))}
            </div>
          )}

          {isFeatureMessage ? (
            <>
              {/* What the feature decides: kept reachable, out of the way. */}
              <button type="button" className={`${styles.moreToggle} body-sm-semibold`} onClick={() => setMoreOpen((v) => !v)} aria-expanded={moreOpen}>
                {moreOpen ? t('systemMessages.editor.less') : t('systemMessages.editor.more')}
              </button>
              {moreOpen && (
                <div className={styles.more}>
                  {eyebrowField}
                  {typeAndIcon}
                </div>
              )}
            </>
          ) : (
            <>
              {eyebrowField}
              {typeAndIcon}
            </>
          )}

          {/*
           * When the message may be seen. On a feature's announcement the flag decides when it starts, and
           * the dates narrow it further - most often an end, so news stops being news.
           */}
          <div className={styles.field}>
            <div className={styles.validityLine}>
              <span className={`${styles.label} body-sm-semibold`}>{t('systemMessages.validity.title')}</span>
              {validityOpen ? null : (
                <>
                  <span className="body-sm-regular">{validityLabel()}</span>
                  <button type="button" className={`${styles.flagLink} body-sm-semibold`} onClick={() => setValidityOpen(true)}>{t('systemMessages.editor.change')}</button>
                </>
              )}
            </div>
            {validityOpen && (
              <div className={styles.row}>
                <div className={styles.field}>
                  <label htmlFor="sm-valid-from" className={`${styles.label} body-xs-sm-regular`}>{t('systemMessages.validFrom')}</label>
                  <input id="sm-valid-from" type="date" className={`${styles.input} body-sm-regular`} value={validFrom} onChange={(e) => { setValidFrom(e.target.value); clearError(); }} dir="ltr" />
                </div>
                <div className={styles.field}>
                  <label htmlFor="sm-valid-to" className={`${styles.label} body-xs-sm-regular`}>{t('systemMessages.validTo')}</label>
                  <input id="sm-valid-to" type="date" className={`${styles.input} body-sm-regular`} value={validTo} onChange={(e) => { setValidTo(e.target.value); clearError(); }} dir="ltr" />
                </div>
              </div>
            )}
            {fieldError('dates')}
          </div>

          {/* Off: the message goes out to nobody - for a feature's, not even when its flag opens. */}
          <ToggleSwitch
            label={isFeatureMessage ? t('systemMessages.dialog.isActiveFeature') : t('systemMessages.dialog.isActive')}
            checked={isActive}
            onChange={setIsActive}
          />

          {/* Off = the message is live, but waits in the bell instead of blocking the screen on sign-in. */}
          <ToggleSwitch label={t('systemMessages.dialog.showAsPopup')} checked={showAsPopup} onChange={setShowAsPopup} />

          {fieldError('save')}

          <div className={styles.actions}>
            <Button variant="white" onClick={handleClose} disabled={isSubmitting}>{returnsToRelease ? t('systemMessages.dialog.backWithoutSaving') : t('close')}</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? t('saving') : returnsToRelease ? t('systemMessages.dialog.saveAndBack') : t('save')}</Button>
          </div>
        </div>

        <div className={styles.previewPane}>
          <div className={styles.previewHead}>
            <h3 className={`${styles.sectionTitle} body-lr-semibold`}>{t('systemMessages.editor.previewSection')}</h3>
            {/* Every surface the message reaches, one at a time. */}
            <div className={styles.surfaces} role="tablist">
              {(['popup', 'bell', ...(tour.length > 0 ? ['bubble' as const] : [])] as Surface[]).map((s) => (
                <button key={s} type="button" role="tab" aria-selected={surface === s} className={`${styles.surface} ${surface === s ? styles.surfaceActive : ''} body-xs-semibold`} onClick={() => setSurface(s)}>
                  {t(`systemMessages.editor.surface.${s}`)}
                </button>
              ))}
            </div>
          </div>
          {surface === 'popup' && (
            <div className={styles.previewFrame}>
              <SystemMessageCard message={preview} isPreview />
              <div className={styles.previewFooter}>
                <span className={`${styles.previewPrimary} body-sm-semibold`}>{t('systemMessages.gotIt')}</span>
              </div>
            </div>
          )}
          {surface === 'bell' && <div className={styles.bellFrame}>{bellPreview}</div>}
          {surface === 'bubble' && <div className={styles.bubbleFrame}>{bubblePreview}</div>}
        </div>
      </div>
  );

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }} title={dialogTitle}>
      {content}
    </MainDialogLayout>
  );
};

export default EditOrCreateSystemMessageDialog;
